import { FLIGHT_TEST } from '../services/flightBookingAPI';

/**
 * Utility to construct payload for the travel search WebSocket.
 * 
 * Rules:
 * lat_long = 1 if using coordinates
 * lat_long = 0 if using station/airport code
 * fetch_avail = 0 for testing
 * 
 * Train only → modes = [1]
 * Flight only → modes = [2]
 * Bus only → modes = [3]
 * If Train + Flight selected AND non-stop unchecked → add 4
 * max_num_stops: If non-stop checked → 0, else 1
 */
export const buildTravelPayload = ({
    src, // { code, lat, lng, isCoordinate }
    dest, // { code, lat, lng, isCoordinate }
    date, // Date object or "DD-MM-YYYY" string
    modes = [], // ["Train", "Flight", "Bus"]
    nonStop = false,
    prefTrainClass = "",
    prefFlightClass = "",
    trainNum = "",
    childrenAges = [],
    numAdults = 1,
    numChildren = 0,
    numInfants = 0,
    test = true,
    from = "",
    search_session_id = "",
    ...rest
}) => {
    // console.log('rest', nonStop)
    // Mode Logic
    let calculatedModes = [];
    const hasAll = modes.includes("All");
    const hasTrain = hasAll || modes.includes("Train");
    const hasFlight = hasAll || modes.includes("Flight");
    const hasBus = hasAll || modes.includes("Bus");
    const hasMixed = hasAll || modes.includes("Mixed");

    if (hasTrain) calculatedModes.push(1);
    if (hasFlight) calculatedModes.push(2);
    if (hasBus) calculatedModes.push(3);
    if (hasMixed) calculatedModes.push(4);

    // Ensure valid fallback
    if (calculatedModes.length === 0) calculatedModes = [1, 2, 4];

    // max_num_stops logic
    // console.log('DEBUG: buildTravelPayload received nonStop:', nonStop);
    const maxNumStops = (nonStop === true || nonStop === 1 || String(nonStop) === "1" || String(nonStop).toLowerCase() === "true") ? 0 : 1;
    // console.log('DEBUG: calculated maxNumStops:', maxNumStops);

    // Date formatting
    // Date formatting
    let formattedDate = "";
    let dateObj = date;

    // Handle string input or null/undefined
    if (!dateObj) {
        dateObj = new Date();
    } else if (!(dateObj instanceof Date)) {
        // Try parsing string
        const parsed = new Date(dateObj);
        if (!isNaN(parsed)) {
            dateObj = parsed;
        } else {
            // If string is already DD-MM-YYYY, checking it might be complex, 
            // but let's assume if it fails parsing, we might want to default to today 
            // OR keep it if it looks like DD-MM-YYYY.
            // For safety against the specific error "time data ''...", we default to today if invalid.
            dateObj = new Date();
        }
    }

    // Ensure we have a valid Date object now
    if (dateObj instanceof Date && !isNaN(dateObj)) {
        const d = String(dateObj.getDate()).padStart(2, '0');
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const y = dateObj.getFullYear();
        formattedDate = `${d}-${m}-${y}`;
    } else {
        // Ultimate fallback
        const now = new Date();
        const d = String(now.getDate()).padStart(2, '0');
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const y = now.getFullYear();
        formattedDate = `${d}-${m}-${y}`;
    }

    // Determine lat_long flag and source/destination format
    let finalSrc = src.code || "";
    let finalDest = dest.code || "";
    let latLong = 0;

    // Coordinate Logic
    // We strive to use coordinates (lat_long = 1) as much as possible to avoid lookup failures on backend.

    // Helper to check if a value is a valid number (or parsable string)
    const isVal = (n) => n !== undefined && n !== null && !isNaN(parseFloat(n));
    // Check if both lat/lng are present and at least one is non-zero (to avoid [0,0] placeholder)
    const hasRealCoords = (l, g) => isVal(l) && isVal(g) && (parseFloat(l) !== 0 || parseFloat(g) !== 0);

    const srcHasCoords = hasRealCoords(src.lat, src.lng);
    const destHasCoords = hasRealCoords(dest.lat, dest.lng);

    // Final selection logic: Prioritize Code, then Coordinates
    if (src.code) {
        finalSrc = String(src.code);
    } else if (srcHasCoords) {
        finalSrc = [parseFloat(src.lat), parseFloat(src.lng)];
    } else {
        finalSrc = [0, 0];
    }

    if (dest.code) {
        finalDest = String(dest.code);
    } else if (destHasCoords) {
        finalDest = [parseFloat(dest.lat), parseFloat(dest.lng)];
    } else {
        finalDest = [0, 0];
    }

    // Set latLong based on whether any side is using coordinates (Array)
    const srcIsCoord = Array.isArray(finalSrc);
    const destIsCoord = Array.isArray(finalDest);
    latLong = (srcIsCoord || destIsCoord) ? 1 : 0;

    // Helper to strip code in parentheses from name
    const cleanName = (name) => {
        if (!name) return "";
        return name.replace(/\s*\(.*?\)\s*/g, '').trim();
    };

    return {
        action: "websocket-sample",
        body: {
            api_key: "web_dev_1",
            search_session_id: search_session_id,
            src: finalSrc,
            dest: finalDest,
            date: formattedDate,
            pref_train_class: prefTrainClass,
            pref_flight_class: prefFlightClass,
            train_num: trainNum,
            test: test,
            lat_long: latLong,
            max_num_stops: maxNumStops,
            modes: calculatedModes,
            num_adults: numAdults,
            num_children: numChildren,
            num_infants: numInfants,
            fetch_avail: 1, // hardcoded as per requirements
            flight_test: FLIGHT_TEST,
            From: from,
            src_name: cleanName(rest.src_name),
            dest_name: cleanName(rest.dest_name)
        }
    };
};
